(function () {
  'use strict';

  var module = angular.module('uwAgenda.global');
  module.service('SharedAgendaService', [
    '$q',
    '$localStorage',
    '$rootScope',
    'settings',
    'UserService',
    'AdministrationService',
    'SharedAgendaBroadcast',
    'WebsocketService',
    'WebsocketRegisterType',
    'ViewsService',
    'RoleType',
    'UniqueIds',
    'AppConfig',
    'IntegratorTypeEnum',
    SharedAgendaFn,
  ]);

  /* main shared agenda service. called in some directives and controllers */
  function SharedAgendaFn(
    $q,
    $localStorage,
    $rootScope,
    settings,
    UserService,
    AdministrationService,
    SharedAgendaBroadcast,
    WebsocketService,
    WebsocketRegisterType,
    ViewsService,
    RoleType,
    UniqueIds,
    AppConfig,
    IntegratorTypeEnum
  ) {
    ///// private members

    // promise for defining 'Loading' Status
    var loadingDeferred = null;

    var currentOwner = null;

    // loaded agendas and other properties
    var data = {
      selectable: true, // is the logged in user allowed to change/select another agenda-client
      currentAgenda: {}, // the current selected agenda
      client: {}, // complete owner data of the current agenda -> in separate object for better separation
      agendas: [], // list of the agendas of the logged in user !!!! (his own and the shared ones he can access)
      badgeCounts: {}, //object with badge counts for the different parts of the application,
      searchStr: '',
      loggedInUserData: {}
    };

    /* clear data object */
    function clear() {
      data.currentAgenda = {};
      data.client = {};
      data.agendas = [];
      data.badgeCounts = {};
      currentOwner = null;
      data.searchStr = '';
      deselect();
    }

    // load agendas by defined user
    function loadAgendasByUser(userId) {
      return loading(userId);
    } // loadAgendasByUser

    // load agendas by current-logged-in-user
    function loadAgendas() {
      // current logged in user id
      var currentUserId = getLoggedInUserData().id;
      return loading(currentUserId);
    } // loadOwners

    // load agendas from user and select one
    function loading(userId) {
      // New Deferred
      loadingDeferred = $q.defer();

      // load all the connected agendas of the user
      return UserService.getAgendas(userId).then(
        function (result) {
          // no agendas found -> keep current agendas & selected user
          if (result.length === 0) {
            loadingDeferred.resolve(true);
            return;
          }

          // only clear if agendas were found!
          clear();

          data.agendas = result;

          var agendaSelected = false;
          var i = 0;
          var len = data.agendas.length;
          // select current logged in users agenda
          for (i; i < len; i++) {
            var agenda = data.agendas[i];
            if (agenda.owner.id === userId) {
              return select(agenda).then(function () {
                // resolve loading-promise
                loadingDeferred.resolve(true);
                agendaSelected = true;
              });
            } //if
          } // for

          // if own agenda hasn't been found (i.e. for subusers) select the first shared agenda
          if (!agendaSelected && data.agendas.length > 0) {
            return selectFirstAgenda().then(function () {
              // resolve loading-promise
              loadingDeferred.resolve(true);
            });
          } //if
        },
        function () {
          console.error(`Error loading agendas for user id ${userId}`);
          loadingDeferred.resolve(false);
        }
      );
    } // loading

    // is service currently loading the agendas ?
    function isLoading() {
      return loadingDeferred ? loadingDeferred.promise : false;
    }

    // select first agenda in list
    function selectFirstAgenda() {
      return select(data.agendas[0]);
    }

    /* load defined owner of agenda -> user data */
    function loadOwner(id) {
      return UserService.get(id, id, data.currentAgenda.id, true).then(
        function (result) {
          data.client = result;
          // broadcast that the data for the current owner has been loaded
          $rootScope.$broadcast(SharedAgendaBroadcast.AgendaOwnerLoaded);
        }
      );
    } // loadOwner

    /* return the current selected User (Agenda-Owner) */
    function currentUser() {
      if (currentOwner) {
        return currentOwner;
      }

      return data.currentAgenda.owner;
    }
    
    function currentAgendaIsRingphoneHq() {
      if (data.client.uniqueId === undefined) { return false; }
      return data.client.uniqueId.toUpperCase() === UniqueIds.RingphoneHq.value;
    }
    
    function currentPracticeIsRingphoneHq() {
      let ringphoneHqId = getRingphoneHqId();
      return data.agendas.map(agenda => agenda.owner.id).includes(ringphoneHqId);
    }
    
    function getRingphoneHqId() {
      return parseInt(AppConfig.RingphoneHqId);
    }
    
    function currentAgendaIsSuperAdmin() {
      return data.currentAgenda.id === 1;
    }
    
    function getSuperAdminId() {
      return 1;
    }

    function currentUserIsPartOfEyeFileIntegration() {
      for (const agenda of data.agendas) {
        if (agenda.owner.integratorType === IntegratorTypeEnum.Eyefile.value) {
          return true;
        }
      }
      return false;
    }
    
    function currentUserIsPartOfIntegrationWithSynchronization() {
      for (const agenda of data.agendas) {
        if (integratorTypeHasSynchronization(agenda.owner.integratorType)) {
          return true;
        }
      }
      return false;
    }

    function currentUserHasIntegrationWithSynchronization() {
      return integratorTypeHasSynchronization(data.currentAgenda.owner.integratorType);
    }
    
    function integratorTypeHasSynchronization(type) {
      return type === IntegratorTypeEnum.CareconnectGp.value || type === IntegratorTypeEnum.Eyefile.value;
    }

    function currentUserIntegrationMergeState() {
      let mergeState = data.currentAgenda.owner.mergeState;
      if (mergeState) {
        return mergeState;
      }
      
      return 0;
    }
    
    function currentUserIsRingphoneAdmin() {
      let roles = data.client.userRoles;
      if (roles === undefined) { return false; }
      for (let i = 0; i < roles.length; i++) {
        if (roles[i].role.type === RoleType.RingfoonAdmin.value) {
          return true;
        }
      }
      return false;
    }

    function setCurrentUser(user) {
      currentOwner = user;

      // set the current agenda to the selected owner's agenda
      data.agendas.forEach(function(agenda) {
        if (agenda.owner.id === user.id) {
          data.currentAgenda = agenda;
        }
      });

      $rootScope.$broadcast(SharedAgendaBroadcast.AgendaOwnerChanged);
    }

    /* deselect all agendas */
    function deselect() {
      data.currentAgenda = {};

      // deselect
      data.agendas.forEach(function (agenda) {
        agenda.selected = false;
      });
    } // deselect

    // select
    function select(agenda) {
      if (agenda === undefined || agenda === null) {
        return;
      }
      agenda.selected = true;
      data.currentAgenda = agenda;
      // update ui
      return updateUserDependentValues(data.currentAgenda.owner);
    } // select

    /* block user for changing client */
    function block() {
      data.selectable = false;
    }

    /* allow user for changing client */
    function allow() {
      data.selectable = true;
    }

    /* is the current selected agenda the agenda of the current logged-in-user ? */
    function isCurrentAgendaMine() {
      return data.currentAgenda.owner.id === getLoggedInUserData().id;
    } // isCurrentAgendaMine

    /* updates all fields that are dependent from the selected user (branding, badges...) */
    function updateUserDependentValues() {
      // reset
      settings.templateInit();

      // set branding based on selected client and his licence (userDto already contains branding fields)
      // if user has no licence or licence has no branding values set, the default values that have been set in app.js will be used
      if (
        data.currentAgenda.owner.templatePrefix !== null &&
        data.currentAgenda.owner.templatePrefix !== ''
      ) {
        $rootScope.templatePrefix = data.currentAgenda.owner.templatePrefix;
      }

      if (
        data.currentAgenda.owner.pageTitle !== null &&
        data.currentAgenda.owner.pageTitle !== ''
      ) {
        $rootScope.pageTitle = data.currentAgenda.owner.pageTitle;
      }

      // load badges
      return loadBadgeCounts(data.currentAgenda.owner.id);
    }

    // load badge count information
    function loadBadgeCounts(selectedUserId) {
      return AdministrationService.badges(
        getLoggedInUserData().id,
        selectedUserId,
        data.currentAgenda.id,
        $localStorage.CurrentLanguage.id
      ).then(function (result) {
        data.badgeCounts = result;
        // inform badges to update
        $rootScope.$broadcast(SharedAgendaBroadcast.BadgeUpdate);
      });
    }

    // get agenda by id
    function getAgenda(agendaId) {
      var i = 0;
      var len = data.agendas.length;
      for (i = 0; i < len; i++) {
        if (data.agendas[i].id === agendaId) {
          return data.agendas[i];
        } //if
      } // for
    } // agenda

    // get agenda id for agenda owner
    function getAgendaByOwner(userId) {
      var i = 0;
      var len = data.agendas.length;

      for (i; i < len; i++) {
        if (data.agendas[i].owner.id === userId) {
          return data.agendas[i];
        }
      }
      return null;
    }
    
    function setLoggedInUserData(loggedInUserData) {
      data.loggedInUserData = loggedInUserData;
    }
    
    function getLoggedInUserData() {
      return data.loggedInUserData;
    }

    // register to websocket events (badge update fe)
    function registerWebsocket() {
      // notification service (websocket) -> badge-update listener
      WebsocketService.register(
        WebsocketRegisterType.Badges,
        function (message) {
          // do work after one second delay
          setTimeout(function () {
            // reload badges from backend
            loadBadgeCounts(data.currentAgenda.owner.id).then(function () {
              // inform badges to update
              $rootScope.$broadcast(SharedAgendaBroadcast.BadgeUpdate);
            });
          }, 1000);
        }
      );

      // general agenda settings update/save -> reload shared-agendas
      WebsocketService.register(
        WebsocketRegisterType.AgendaSettings,
        function (message) {
          // reload shared-agendas

          // if ringfoon -> load agendas of current-agenda
          // if not ringfoon -> load agendas of current-logged-in-user

          if ($rootScope.isRingfoon) {
            // Only If updated-id is relevant for me
            var userId = parseInt(message.data.userId);
            var len = data.agendas.length;
            var relevant = false;
            for (var i = 0; i < len; i++) {
              if (data.agendas[i].owner.id === userId) {
                relevant = true;
                break;
              }
            } // for

            if (relevant) {
              // get current client-id
              var clientId = data.client ? data.client.id : 0;

              loadAgendasByUser(data.currentAgenda.owner.id).then(function (
                result
              ) {
                // update ui (inform ui)
                $rootScope.$broadcast(SharedAgendaBroadcast.UpdateUI);

                if (clientId === userId) {
                  if (data.client) {
                    loadOwner(clientId);
                  }
                } //if
              });
            } // if
          } // if
          else {
            // current selected agenda id
            var currentAgendaId = data.currentAgenda.id;
            // reload all the shared-agendas
            loadAgendas().then(function (result) {
              // select current shared-agenda (if available)
              var agenda = getAgenda(currentAgendaId);

              if (agenda) {
                deselect();
                select(agenda);
                // update ui (inform ui)
                $rootScope.$broadcast(SharedAgendaBroadcast.UpdateUI);
              } // if
            });
          } // else
        }
      );
    } // registerWebsocket

    this.getAgendasShownInDayView = function () {
      var currentView = ViewsService.getCurrentView();
      if (!currentView) {
        return [];
      }

      var viewUsers = currentView.viewUsers.filter(
        (viewUser) => viewUser.showInAgendaDayView
      );
      viewUsers.sort((v1, v2) => v1.sortOrder - v2.sortOrder);

      return viewUsers
        .map(
          (viewUser) =>
            data.agendas.filter(
              (agenda) =>
                agenda.owner.id === viewUser.userId &&
                agenda.isAgendaAccessibleToCurrentUser
            )[0]
        )
        .filter((agenda) => !!agenda);
    };

    this.getAgendasShownInWeekView = function () {
      var currentView = ViewsService.getCurrentView();
      if (!currentView) {
        return [];
      }

      var viewUsers = currentView.viewUsers.filter(
        (viewUser) => viewUser.showInAgendaWeekView
      );
      viewUsers.sort((v1, v2) => v1.sortOrder - v2.sortOrder);

      return viewUsers
        .map(
          (viewUser) =>
            data.agendas.filter(
              (agenda) =>
                agenda.owner.id === viewUser.userId &&
                agenda.isAgendaAccessibleToCurrentUser
            )[0]
        )
        .filter((agenda) => !!agenda);
    };

    this.getAgendasShownInNewAppointments = function () {
      var currentView = ViewsService.getCurrentView();
      if (!currentView) {
        return [];
      }

      var viewUsers = currentView.viewUsers.filter(
        (viewUser) => viewUser.showInNewAppointments
      );
      viewUsers.sort((v1, v2) => v1.sortOrder - v2.sortOrder);

      return viewUsers
        .map(
          (viewUser) =>
            data.agendas.filter(
              (agenda) =>
                agenda.owner.id === viewUser.userId &&
                agenda.isAgendaAccessibleToCurrentUser
            )[0]
        )
        .filter((agenda) => !!agenda);
    };

    this.getAgendasShownInFreeAppointments = function () {
      var currentView = ViewsService.getCurrentView();
      if (!currentView) {
        return [];
      }

      var viewUsers = currentView.viewUsers.filter(
        (viewUser) => viewUser.showInFreeAppointments
      );
      viewUsers.sort((v1, v2) => v1.sortOrder - v2.sortOrder);

      return viewUsers
        .map(
          (viewUser) =>
            data.agendas.filter(
              (agenda) =>
                agenda.owner.id === viewUser.userId &&
                agenda.isAgendaAccessibleToCurrentUser
            )[0]
        )
        .filter((agenda) => !!agenda);
    };

    this.getDayTasksAgendas = function () {
      var currentView = ViewsService.getCurrentView();
      if (!currentView) {
        return [];
      }

      var ownerIds = currentView.viewUsers
        .filter((viewUser) => viewUser.showDayTasks)
        .map((viewUser) => viewUser.userId);

      return data.agendas.filter(
        (agenda) =>
          ownerIds.indexOf(agenda.owner.id) >= 0 &&
          agenda.isAgendaAccessibleToCurrentUser
      );
    };

    ///// extern
    this.clear = clear;
    this.loadAgendas = loadAgendas;
    this.loadOwner = loadOwner;
    this.currentUser = currentUser;
    this.currentAgendaIsRingphoneHq = currentAgendaIsRingphoneHq;
    this.currentPracticeIsRingphoneHq = currentPracticeIsRingphoneHq;
    this.getRingphoneHqId = getRingphoneHqId;
    this.currentAgendaIsSuperAdmin = currentAgendaIsSuperAdmin;
    this.getSuperAdminId = getSuperAdminId;
    this.currentUserIsRingphoneAdmin = currentUserIsRingphoneAdmin;
    this.setCurrentUser = setCurrentUser;
    this.deselect = deselect;
    this.select = select;
    this.block = block;
    this.allow = allow;
    this.isCurrentAgendaMine = isCurrentAgendaMine;
    this.getAgenda = getAgenda;
    this.getAgendaByOwner = getAgendaByOwner;
    this.registerWebsocket = registerWebsocket;
    this.loadAgendasByUser = loadAgendasByUser;
    this.isLoading = isLoading;
    this.currentUserIsPartOfIntegrationWithSynchronization = currentUserIsPartOfIntegrationWithSynchronization;
    this.currentUserHasIntegrationWithSynchronization = currentUserHasIntegrationWithSynchronization;
    this.currentUserIntegrationMergeState = currentUserIntegrationMergeState;
    this.currentUserIsPartOfEyeFileIntegration = currentUserIsPartOfEyeFileIntegration;
    this.setLoggedInUserData = setLoggedInUserData;
    this.getLoggedInUserData = getLoggedInUserData;

    // for access the array
    this.data = data;
  }
})();
